import { PapiNcBucketHeader, PapiUser } from '@wix/da-papi-types';
import { Action } from 'redux-actions';
import { ModerationReportNotification } from './nc';

export type SelectionMode = 'all' | 'some';
export type MoveOperation = 'save' | 'remove' | 'feed';

/**
 * Because we load the notification center redux dynamically, we have to initialize a few things before our notifications
 * are ready to be displayed. This enum is used to track those things (e.g. are translations initializing?)
 */
export enum InitializationStatus {
  /**
   * We're still waiting on something (e.g. code to execute, network request to finish)
   */
  Initializing = 'initializing',
  /**
   * Initialized and ready to go!
   */
  Success = 'success',
  /**
   * Most likely we failed to load a resource due to a network issue.
   */
  Failure = 'failure',
}

export interface DrawerStreamParams {
  folder?: number;
}

export interface BucketStreamParams {
  folder?: number;
  bucket: string;
  filterId?: number;
}

export interface CommentStreamParams {
  messageId?: string;
}

export type SystemNotificationType = '@FBS_Save' | '@FBS_Remove' | '@FBS_Feed';

export interface IdentityItem {
  newCount?: number;
  gruser: PapiUser;
  inboxFolderId?: number;
  savedFolderId?: number;
  removedFolderId?: number;
}

export interface CategoryFilter {
  filterId: number;
  filterName: string;
}

export interface DeveloperSettings {
  useMock?: boolean;
  userIdOverride?: number;
}

export interface NotificationsState {
  /**
   * Because we load the notification center and its resources dynamically, we have to keep track of those resources.
   */
  initialization: {
    isReduxInitialized: InitializationStatus;
    areTranslationsInitialized: InitializationStatus;
  };
  bucketData: {
    [bucket: string]: {
      header: PapiNcBucketHeader;
      filters?: CategoryFilter[];
    };
  };
  messageState: {
    [messageId: string]: {
      isLoading?: boolean;
      isDeletedAfterReply?: boolean;
    };
  };
  viewOptions: {
    activeFolder?: number;
    /** Information regarding the user's currently open bucket. Tells us what subnav option they're filtering by or what the bucket's id is. */
    activeBucket?: {
      /** The bucket's id */
      bucket: string;
      /** In cases where the bucket is for a single notification, this field holds said message's id  */
      message?: string;
      /** The current subnav item the user has set */
      filterId?: number;
    };
    showShortcuts?: boolean;
  };
  selection?: {
    mode: SelectionMode;
    selectedIds: string[];
  };
  isBadgeGiven?: {
    [username: string]: boolean;
  };
  adom?: {
    reportedUserids: number[];
  };
  drawer?: {
    hasGroups?: boolean;
  };
  messageSeen?: {
    messageIds: string[];
  };
  messageUnseen?: {
    messageIds: string[];
  };
  messageUpdateQueue?: {
    actions: Action<any>[]; // queue of actions
  };
  developerSettings?: DeveloperSettings;
  messageFocus?: string; // Used for keyboard shortcuts focus
  messageReplyFocus?: string; // Used for reply from dropdown
}

export const moderationCaseTypes: {
  [key: string]: ModerationReportNotification['messageData']['correspondence']['bppModule']['moderationReport']['caseType'];
} = {
  mature: 'Mature',
  copyright: 'Copyright Violation',
  policy: 'Policy Violation',
  miscat: 'Miscategorization',
  pornography: 'Content Violation',
  underage: 'Underage Sexualized Content',
  malware: 'Malware',
  warez: 'Warez',
  virus: 'Virus',
  password: 'Password',
  premium: 'Premium Content Issue',
  spam: 'Spam',
  alarming: 'Alarming',
  abuse: 'Abuse',
  copyright_thirdparty: 'Third Party Copyright Violation',
};

export enum FeedbackModalType {
  CORRESPONDENCE = 'CORRESPONDENCE',
}
